<template>
  <Modal
    v-model:visible="isVisible"
    class="bill-to-edit-modal bill-to"
    size="md"
    @keypress.esc="onCancel"
    @keypress.enter="onSave"
  >
    <template #header>
      <div class="bill-to-edit-modal-header">
        <LabeledIcon
          class="bill-to-edit-modal-header-label"
          icon="pi pi-map-marker"
          :label="t('common.billTos')"
        >
          <template #label="{ label }">
            <span class="p-dialog-title">{{ label }}</span>
          </template>
        </LabeledIcon>

        <div class="bill-to-edit-modal-header-address">
          <div class="header-left">
            <span class="location-name">
              {{
                internalValue.id
                  ? internalValue.location?.locationName
                  : t('common.newParam', { param: t('common.billTos') })
              }}
            </span>

            <RygenButton
              v-if="editable"
              mode="tertiary"
              :label="
                internalValue.id
                  ? t('common.changes')
                  : t('address.addressBook')
              "
              @click="onShowAddressBook"
            />
          </div>

          <RygenButton
            v-if="editable && (internalValue.id || v$.$anyDirty)"
            mode="tertiary"
            :label="t('orders.clearAndStartOver')"
            @click="onClear"
          />
        </div>
      </div>
    </template>

    <template #default>
      <div class="bill-to-edit-modal-body flex flex-column flex-gap-1">
        <ShippingPointLocationEdit
          :id="`${id}-bill-to`"
          ref="locationEditRef"
          v-model="internalValue.location"
          :location-type="LocationTypeWellKnownNaturalId.BILL_TO"
          :disabled="!editable"
          append-to="body"
          @address-book-select="onLocationSelect"
          @autocomplete="onAutocompleteSelected"
          @saving="savingLocationGroup = $event"
        >
          <template #extra-input="{ disabled, isUneditableFiconLocation }">
            <InputWrapper
              v-slot="{ ariaProps, inputClasses, inputId, invalid }"
              :errors="v$.billToDetails.invoicePayingParty.$errors"
              class="w-3"
              input-id="`${id}-bill-to-invoice-paying-party`"
              :label="t('common.invoicePayingParties')"
            >
              <BillToInvoicePayingPartyDropdown
                :id="inputId"
                v-model="internalValue.billToDetails.invoicePayingParty"
                v-bind="ariaProps"
                :class="inputClasses"
                :invalid="invalid"
                :disabled="disabled || isUneditableFiconLocation"
                append-to="body"
                :placeholder="t('common.invoicePayingParties')"
              />
            </InputWrapper>
          </template>
        </ShippingPointLocationEdit>
      </div>

      <AddressValidationResultModal
        v-model:visible="isShowingAddressValidationModal"
        :validation-result="validationResult.data"
        class="bill-to"
        @accept-corrected-address="onAcceptCorrection"
        @keep-original-address="onKeepOriginalAddress"
      />
    </template>

    <template #footer>
      <RygenButton
        mode="tertiary"
        :disabled="validationResult.loading"
        :label="t('common.cancel')"
        @click="onCancel"
      />
      <RygenButton
        v-if="editable"
        mode="primary"
        icon="pi pi-save"
        :disabled="validationResult.loading || savingLocationGroup"
        :label="t('common.saveParam', { param: t('common.billTos') })"
        :loading="validationResult.loading || savingLocationGroup"
        @click="onSave"
      />
    </template>
  </Modal>
</template>

<script lang="ts" setup>
import axios from 'axios';
import isEqual from 'lodash/isEqual';
import type { PropType, Ref } from 'vue';
import { nextTick, ref, toRefs, useTemplateRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { required, useValidations } from '@rygen/rygen-ui/composables';
import {
  AddressValidationResult,
  BillToDetails,
  Loadable,
  Location,
  LocationTypeWellKnownNaturalId,
} from '@rygen/rygen-ui/model';

import api from '@/api';
import { BillTo, EmbeddableLocation, OrderBillTo } from '@/model';

import BillToInvoicePayingPartyDropdown from '@/components/location/BillToInvoicePayingPartyDropdown.vue';
import ShippingPointLocationEdit from '@/components/order/ShippingPointLocationEdit.vue';
import LabeledIcon from '@/components/ui/LabeledIcon.vue';
import AddressValidationResultModal from '@rygen/rygen-ui/components/address/AddressValidationResultModal.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import Modal from '@rygen/rygen-ui/components/ui/Modal.vue';

const isVisible = defineModel('visible', {
  type: Boolean,
  default: () => false,
});

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  billTo: {
    type: Object as PropType<BillTo>,
    default: () => new OrderBillTo(),
  },
  editable: {
    type: Boolean,
    default: () => false,
  },
});
const { id, billTo, editable } = toRefs(props);
const emit = defineEmits<{
  (event: 'cancel'): void;
  (event: 'save', billTo: BillTo): void;
  (event: 'show-address-book-modal'): void;
}>();

const locationEditRef = useTemplateRef('locationEditRef');

const internalValue: Ref<BillTo> = ref(new OrderBillTo());

const { t } = useI18n();
const { v$, validate } = useValidations(
  internalValue,
  {
    billToDetails: {
      invoicePayingParty: {
        required: required(t('common.invoicePayingParties')),
      },
    },
  },
  id.value,
);

const shouldValidate = ref(true);
const validationResult: Ref<Loadable<AddressValidationResult>> = ref({
  loading: false,
});
const isShowingAddressValidationModal = ref(false);
const savingLocationGroup = ref(false);

const onCancel = () => {
  const Clazz = Object.getPrototypeOf(billTo.value).constructor;
  internalValue.value = new Clazz(billTo.value);
  emit('cancel');
  isVisible.value = false;
};

const onClear = () => {
  const Clazz = Object.getPrototypeOf(billTo.value).constructor;
  internalValue.value = new Clazz({
    id: billTo.value?.id,
    location: new EmbeddableLocation(),
  });
  nextTick(() => v$.value.$reset());
};

const validateLocation = async (): Promise<boolean> => {
  try {
    validationResult.value.loading = true;
    if (!internalValue.value.location || !shouldValidate.value) {
      return true;
    }

    // We only care about adhoc locations, not saved locations
    const isAdhocLocation = !internalValue.value.location.locationId;
    const isChanged = !isEqual(
      internalValue.value.location,
      billTo.value.location,
    );
    if (!isAdhocLocation || !isChanged) {
      return true;
    }

    const address = internalValue.value.location.toAddress();
    validationResult.value.data =
      await api.shippingPoint.validateAddress(address);

    if (validationResult.value.data.errors?.length) {
      isShowingAddressValidationModal.value = true;
      return false;
    }
  } catch (e) {
    if (axios.isCancel(e)) {
      return false;
    }

    console.error('Error validating address', e);
  } finally {
    validationResult.value.loading = false;
  }

  return true;
};

/**
 * Save the current internal billTo
 *
 * This method will fire the events to save the internal bill to to the parent
 * order/shipment, so it should only be called after the internal bill to has
 * been fully validated.
 */
const saveValidatedBillTo = () => {
  emit('save', internalValue.value);
  isVisible.value = false;
};

const onSave = async () => {
  if (!editable.value) {
    onCancel();
    return;
  }

  if ((await validate()) && (await validateLocation())) {
    saveValidatedBillTo();
  }
};

const onShowAddressBook = () => {
  locationEditRef.value?.cancelExistingAutocompleteRequest();
  emit('show-address-book-modal');
};

const onAutocompleteSelected = (isAutocompleteSelection: boolean) => {
  internalValue.value.location.hasBeenValidated = isAutocompleteSelection;
};

const onAcceptCorrection = async () => {
  internalValue.value.location = EmbeddableLocation.fromAddress(
    validationResult.value.data?.correctedAddress?.address,
    internalValue.value.location?.locationName,
  );
  isShowingAddressValidationModal.value = false;
  if (await validate()) {
    saveValidatedBillTo();
  }
};

const onKeepOriginalAddress = async () => {
  internalValue.value.location = EmbeddableLocation.fromAddress(
    validationResult.value.data?.originalAddress,
    internalValue.value.location?.locationName,
  );
  isShowingAddressValidationModal.value = false;
  if (await validate()) {
    saveValidatedBillTo();
  }
};

const onLocationSelect = async (location: Location) => {
  if (!location.id) {
    return;
  }

  internalValue.value.location = EmbeddableLocation.fromLocation(location);
  internalValue.value.billToDetails = new BillToDetails(location.billToDetails);
};

watch(
  internalValue,
  (newVal) => {
    // We only care about adhoc locations, not saved locations
    const isAdhocLocation =
      !!internalValue.value.location &&
      internalValue.value.location.locationId === undefined;
    const isChanged = !isEqual(newVal.location, billTo.value.location);
    shouldValidate.value = isAdhocLocation && isChanged;
  },
  { deep: true, immediate: true },
);

watch(isVisible, (newVal) => {
  if (newVal) {
    const Clazz = Object.getPrototypeOf(billTo.value).constructor;
    internalValue.value = new Clazz(billTo.value);
    v$.value.$reset();
  }
});
</script>

<style>
.bill-to-edit-modal.rygen-modal.p-dialog {
  --p-dialog-color: var(--p-gray-600);
  --p-dialog-title-font-weight: 500;

  border: 0;

  .p-dialog-header {
    border-left-style: solid;
    border-left-width: var(--sp-1);
    border-left-color: var(--stop-type-accent-color);
    border-radius: var(--p-border-radius-sm) var(--p-border-radius-sm) 0 0;

    .modal-header {
      width: 100%;

      .bill-to-edit-modal-header {
        display: flex;
        gap: var(--gap-1);
        width: 100%;

        .rygen-button {
          flex: 0 0 auto;
        }

        .bill-to-edit-modal-header-label {
          display: flex;
          gap: var(--gap-1);
          align-items: center;
          flex: 1 0 auto;

          .pi {
            font-size: 21px;
          }
        }

        .bill-to-edit-modal-header-address {
          display: flex;
          gap: var(--gap-1);
          justify-content: space-between;
          align-items: center;
          width: 100%;
          /* Keeps this div from overflowing */
          min-width: 0;

          .header-left {
            display: flex;
            gap: var(--gap-1);
            align-items: center;
            /* Keeps this div from overflowing */
            min-width: 0;

            .location-name {
              font-weight: 400;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }
          }
        }
      }
    }
  }

  .p-dialog-content {
    display: flex;
    flex-flow: column;
    padding: 0;

    .bill-to-edit-modal-body {
      padding: var(--sp-6) var(--sp-12);
    }
  }
}
</style>
